import type { ImageProps } from '@archipro-design/aria';
import {
    useStyles,
    Grid,
    VideoPlayer,
    Image,
    Label,
    Caption,
    useAppDisplayModeContext,
} from '@archipro-design/aria';

import * as S from './VideoGrid.style';

import type { VideoTileProp } from '@modules/professional/type/company-videos';
import { COMPANY_VIDEO_CROP } from '@modules/professional/config/constants';
import type { SyntheticEvent } from 'react';
import { useSearchParams } from '@remix-run/react';
import { useTracker } from '@archipro-website/tracker';

interface VideoGridProp {
    videos: VideoTileProp[];
    onTileClick?: (e: SyntheticEvent, tile: VideoTileProp) => void;
}

const VideoGrid: React.FC<VideoGridProp> = ({ videos, onTileClick }) => {
    const { css } = useStyles();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const [searchParams, setSearchParams] = useSearchParams();

    if (!videos?.length) return null;

    return (
        <Grid columns={isMobile ? '100%' : 4} className={css(S.VideoGrid)}>
            {videos.map((video) => (
                <VideoTile
                    key={`videoTile-${video.id}`}
                    {...video}
                    onClick={(e: SyntheticEvent) => {
                        onTileClick?.(e, video);
                        searchParams.set('video', video.id);
                        setSearchParams(searchParams);
                    }}
                    onClose={() => {
                        searchParams.delete('video');
                        setSearchParams(searchParams);
                    }}
                />
            ))}
        </Grid>
    );
};

export default VideoGrid;

const VideoTile: React.FC<VideoTileProp> = (video) => {
    const {
        src,
        caption,
        coverImage,
        onClick,
        onClose,
        disablePlay,
        shouldOpen = false,
    } = video;

    const tracker = useTracker();

    const { css } = useStyles();

    const { mobile: isMobile } = useAppDisplayModeContext();
    const screenSize = isMobile ? 'mobile' : 'desktop';
    const coverImageProps = {
        src: typeof coverImage === 'string' ? coverImage : '',
        alt: caption ?? '',
        width: '100%',
        height: '100%',
        layout: 'fill',
        objectFit: 'cover',
    } as ImageProps;
    return (
        <Grid columns={'100%'} className={css(S.VideoTile)}>
            {disablePlay ? (
                <>
                    {typeof coverImage === 'object' && 'src' in coverImage && (
                        <div
                            className={css(
                                S.VideoPlaceholder(
                                    COMPANY_VIDEO_CROP[screenSize],
                                ),
                            )}
                        >
                            <Image
                                src={coverImage.src}
                                width={COMPANY_VIDEO_CROP[screenSize].width}
                                height={COMPANY_VIDEO_CROP[screenSize].height}
                                alt={caption ?? ''}
                                layout={'fill'}
                                objectFit={'cover'}
                            />
                        </div>
                    )}
                </>
            ) : (
                <VideoPlayer
                    src={src}
                    coverImage={
                        typeof coverImage === 'object'
                            ? { ...coverImage, alt: caption || '' }
                            : coverImageProps
                    }
                    playing={shouldOpen}
                    height={COMPANY_VIDEO_CROP[screenSize].height}
                    controls={true}
                    loop={false}
                    playsInline={false}
                    variables={{
                        playIconSizeDesktop: 56,
                        playIconSizeMobile: 56,
                    }}
                    onClick={onClick}
                    onClose={onClose}
                    trackPlayEvent={() => {
                        tracker.log('Video', {
                            url: new URL(window.location.href),
                        });
                    }}
                />
            )}
            {isMobile ? (
                <Caption variant="03" variables={{ lineHeight: 1.5 }}>
                    {caption}
                </Caption>
            ) : (
                <Label variant="04">{caption}</Label>
            )}
        </Grid>
    );
};
